<template>
    <div class="tw:space-y-4">
        <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
            <div class="tw:inline-flex tw:items-center tw:gap-1">
                <span class="tw:text-xs tw:text-emerald-700">{{ 'Cómo funciona' }}</span>
                <Tooltip :text="tooltipExplicacion" variant="success" />
            </div>
            <div class="tw:inline-flex tw:items-center tw:gap-1 ">
                <span class="tw:text-xs tw:text-amber-700">{{ 'Cuándo se puede modificar' }}</span>
                <Tooltip :text="tooltipRestriccion" variant="warning" />
            </div>
        </div>

        <div class="tw:grid tw:grid-cols-1 tw:md:grid-cols-2 tw:gap-4">
            <BaseMultiselect
                :label="'Tipo de abono'"
                name="tipo_abono"
                :value="tipoSeleccionado"
                :options="tipos"
                label-key="descripcion"
                value-key="id"
                :disabled="isDisabled"
                custom-class="tw:w-full"
                @input="val => tipoSeleccionado = val"
            />

            <BaseMultiselect
                :label="'Precios'"
                name="precios"
                :value="preciosSeleccionados"
                :options="descuentosEdad"
                label-key="descripcion"
                value-key="id"
                :multiple="true"
                :disabled="isDisabled || !tipoSeleccionado"
                custom-class="tw:w-full"
                @input="val => preciosSeleccionados = val"
            />

            <div v-if="tipoSeleccionado && preciosSeleccionados.length" class="tw:flex tw:items-end">
                <Button
                    variant="primary"
                    :disabled="isDisabled"
                    @click="addCombinacion"
                >
                    {{ 'Añadir' }}
                </Button>
            </div>
        </div>

        <div v-if="combinaciones.length">
            <hr class="tw:my-4" />

            <div class="tw:overflow-x-auto">
                <table class="tw:w-full tw:text-sm">
                    <thead>
                        <tr class="tw:border-b tw:border-gray-200">
                            <th class="tw:py-2 tw:px-1.5 tw:font-semibold tw:text-gray-600 tw:text-xs tw:uppercase">
                                {{ 'Tipo de abono' }}
                            </th>
                            <th class="tw:py-2 tw:px-1.5 tw:font-semibold tw:text-gray-600 tw:text-xs tw:uppercase">
                                {{ 'Precios asociados' }}
                            </th>
                            <th class="tw:py-2 tw:px-1.5 tw:font-semibold tw:text-gray-600 tw:text-xs tw:uppercase">
                                {{ 'Acciones' }}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(comb, combIdx) in combinaciones"
                            :key="comb.tipo.id"
                            class="tw:border-b tw:border-gray-100 tw:hover:bg-gray-50"
                        >
                            <td class="tw:py-3 tw:px-1.5 tw:text-xs">
                                {{ comb.tipo.descripcion }}{{ comb.tipo.renovacion ? ' - (Renovación)' : '' }}
                            </td>
                            <td class="tw:py-3 tw:px-1.5">
                                <div class="tw:flex tw:flex-wrap tw:gap-1">
                                    <span
                                        v-for="(desc) in comb.descuentos"
                                        :key="desc.id"
                                        class="tw:inline-flex tw:items-center tw:gap-1 tw:px-2 tw:py-1 tw:bg-sky-100 tw:text-sky-900 tw:rounded-md tw:text-xs"
                                    >
                                        <span>{{ customLabel(desc) }}</span>
                                    </span>
                                </div>
                            </td>
                            <td class="tw:py-3 tw:px-1.5 tw:text-center">
                                <button
                                    v-if="!isDisabled"
                                    type="button"
                                    class="tw:text-red-500 tw:hover:text-red-700"
                                    :title="'Eliminar tipo completo y todos sus descuentos'"
                                    @click="removeCombinacion(combIdx)"
                                >
                                    <i class="fas fa-trash"></i>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
    import store from '@/store';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import Tooltip from '@/modules/admin/ui/Tooltip.vue';

    const props = defineProps({
        evento: { type: Object, required: true },
        tipos: { type: Array, default: () => [] }
    });

    const emit = defineEmits(['updateEvento']);

    // Textos informativos que estaban en el FormEvento legacy de master
    // (líneas 3-9 del `DescuentosAbonados.vue` legacy) como `<span><b>`,
    // más la info de los labels de los BaseMultiselect. Englobados aquí en
    // 2 tooltips para no repetir texto en la cabecera de la sección.
    const tooltipExplicacion = 'Aquí defines qué butacas de abonados NO se cerrarán. Selecciona primero el tipo de abono y luego los precios del mismo que quieras excluir del cierre. Las butacas de los abonados que tengan ese tipo de abono y precio NO SE CERRARÁN.';
    const tooltipRestriccion = 'Este apartado solo se podrá modificar si el evento NO ESTÁ PUBLICADO.';

    const localData = ref(JSON.parse(JSON.stringify(props.evento)));
    const combinaciones = ref([]);
    const tipoSeleccionado = ref(null);
    const preciosSeleccionados = ref([]);
    const descuentosEdad = ref([]);
    const loading = ref(false);

    const isDisabled = computed(() => props.evento.status?.id === 1);

    const instance = getCurrentInstance();

    function customLabel(option) {
        if (option.renovacion) {
            return `${option.descripcion} - (Renovación)`;
        }
        return option.descripcion;
    }

    function getDescuentos(tipo) {
        loading.value = true;
        const alias = store.getters.getConfig?.getVuexAlias?.('back.precios.descuentos') || 'BACK_PRECIOS_DESCUENTOS';
        const params = {
            filters: { to_select: true },
            lista_id: store.getters.getListaId,
            precio_id: tipo.id
        };
        store.dispatch(alias, params).then(res => {
            descuentosEdad.value = res.data.data || [];
        }).finally(() => {
            loading.value = false;
        });
    }

    function addCombinacion() {
        const tipoId = tipoSeleccionado.value;
        const precioIds = preciosSeleccionados.value;
        if (!tipoId || !precioIds.length) return;

        const tipo = props.tipos.find(t => t.id === tipoId);
        if (!tipo) return;

        const descuentos = descuentosEdad.value.filter(d => precioIds.includes(d.id));
        if (!descuentos.length) return;

        const existente = combinaciones.value.find(c => c.tipo.id === tipoId);
        if (existente) {
            descuentos.forEach(desc => {
                if (!existente.descuentos.some(d => d.id === desc.id)) {
                    existente.descuentos.push(desc);
                }
            });
        } else {
            combinaciones.value.push({
                tipo,
                descuentos: [...descuentos]
            });
        }

        descuentosEdad.value = [];
        tipoSeleccionado.value = null;
        preciosSeleccionados.value = [];
        emitUpdate();
    }

    function removePrecio(combinacionIndex, precioIndex) {
        const comb = combinaciones.value[combinacionIndex];
        if (!comb) return;
        comb.descuentos.splice(precioIndex, 1);
        if (comb.descuentos.length === 0) {
            combinaciones.value.splice(combinacionIndex, 1);
        }
        emitUpdate();
    }

    function removeCombinacion(index) {
        combinaciones.value.splice(index, 1);
        emitUpdate();
    }

    function emitUpdate() {
        const flatDescuentos = [];
        combinaciones.value.forEach(comb => {
            comb.descuentos.forEach(desc => {
                flatDescuentos.push({
                    tipo_abono: { id: comb.tipo.id, descripcion: comb.tipo.descripcion },
                    id: desc.id,
                    descripcion: desc.descripcion,
                    precio: desc.precio
                });
            });
        });
        // Only emit the keys this component actually owns. Emitting the
        // whole localData (a stale deep clone from setup) makes the
        // parent's Object.assign overwrite fields the user just changed
        // in the parent itself (e.g. cerrar_butacas_abonados_no_confirmados
        // — toggled outside this component).
        emit('updateEvento', {
            descuentos_abonados_no_cerrar_butacas: flatDescuentos,
            no_cerrar_butacas_combinaciones: combinaciones.value
        });
    }

    watch(tipoSeleccionado, newVal => {
        if (newVal != null) {
            const tipo = props.tipos.find(t => t.id === newVal);
            if (tipo) getDescuentos(tipo);
        }
        const existente = newVal != null
            ? combinaciones.value.find(c => c.tipo.id === newVal)
            : undefined;
        preciosSeleccionados.value = existente
            ? existente.descuentos.map(d => d.id)
            : [];
    });

    watch(preciosSeleccionados, newIds => {
        const tipoId = tipoSeleccionado.value;
        if (tipoId == null) return;

        const combinacion = combinaciones.value.find(c => c.tipo.id === tipoId);
        if (!combinacion) return;

        if (!newIds.length) {
            const idx = combinaciones.value.findIndex(c => c.tipo.id === tipoId);
            if (idx !== -1) removeCombinacion(idx);
            return;
        }

        const eliminados = combinacion.descuentos.filter(d => !newIds.includes(d.id));
        eliminados.forEach(d => {
            const combIdx = combinaciones.value.findIndex(c => c.tipo.id === tipoId);
            const descIdx = combinacion.descuentos.findIndex(dd => dd.id === d.id);
            if (combIdx !== -1 && descIdx !== -1) removePrecio(combIdx, descIdx);
        });
    }, { deep: true });

    onMounted(() => {
        if (localData.value.descuentos_abonados_no_cerrar_butacas?.length) {
            const map = new Map();
            localData.value.descuentos_abonados_no_cerrar_butacas.forEach(d => {
                if (!map.has(d.tipo_abono.id)) {
                    map.set(d.tipo_abono.id, { tipo: d.tipo_abono, descuentos: [] });
                }
                map.get(d.tipo_abono.id).descuentos.push({
                    id: d.id,
                    descripcion: d.descripcion,
                    precio: d.precio
                });
            });
            combinaciones.value = [...map.values()];
        }
        emitUpdate();
    });
</script>
